// Quest System - Turkish Mythology ARPG
// Defines quests and their objectives

export type QuestStatus = 'locked' | 'available' | 'active' | 'completed';

export type ObjectiveType =
  | 'kill_enemy'      // Kill specific enemy types
  | 'kill_boss'       // Kill a specific boss
  | 'collect_item'    // Collect specific items
  | 'talk_to_npc'     // Talk to an NPC
  | 'reach_zone'      // Enter a specific zone
  | 'reach_level';    // Reach a certain level

export interface QuestObjective {
  id: string;
  type: ObjectiveType;
  description: string;
  target: string;           // Enemy type, item id, npc id, zone id, or level
  required: number;         // Amount needed
  current: number;          // Current progress
  completed: boolean;
}

export interface QuestReward {
  exp?: number;
  gold?: number;
  itemIds?: string[];       // Specific item rewards
  skillPoints?: number;
  passivePoints?: number;
}

export interface Quest {
  id: string;
  name: string;
  description: string;
  act: number;              // Which act this quest belongs to
  chapter: number;          // Order within the act

  status: QuestStatus;
  objectives: QuestObjective[];
  rewards: QuestReward;

  // Prerequisites
  requiredLevel?: number;
  requiredQuests?: string[]; // Quest IDs that must be completed first
  requiredZone?: string;

  // Quest giver
  giverNpcId?: string;
  turnInNpcId?: string;

  // Dialog
  startDialog?: string;
  progressDialog?: string;
  completeDialog?: string;
}

// ==========================================
// MAIN STORY QUESTS
// ==========================================

export const MAIN_QUESTS: Quest[] = [
  // ===================
  // ACT 1 - KARANLIK ORMAN
  // ===================
  {
    id: 'act1_intro',
    name: 'Karanlık Uyanış',
    description: 'Köyün şamanı seni çağırıyor. Karanlık güçler ormanı sardı.',
    act: 1,
    chapter: 1,
    status: 'available',
    objectives: [
      {
        id: 'talk_shaman',
        type: 'talk_to_npc',
        description: 'Şaman Korkut ile konuş',
        target: 'kam_batur',
        required: 1,
        current: 0,
        completed: false
      }
    ],
    rewards: {
      exp: 100,
      gold: 50
    },
    giverNpcId: 'kam_batur',
    startDialog: 'Hoş geldin, genç savaşçı. Karanlık güçler ormanı sardı. Erlik\'in uşakları köyümüze her gün daha çok yaklaşıyor. Senin gücüne ihtiyacımız var.',
    progressDialog: 'Hazır mısın, savaşçı?',
    completeDialog: 'İyi, dinle beni. Ormandaki Kormos yaratıkları giderek çoğalıyor. Onları durdurmamız gerek.'
  },
  {
    id: 'act1_first_blood',
    name: 'İlk Kan',
    description: 'Karanlık Orman\'daki Kormos yaratıklarını temizle.',
    act: 1,
    chapter: 2,
    status: 'locked',
    objectives: [
      {
        id: 'kill_kormos',
        type: 'kill_enemy',
        description: 'Kormos öldür',
        target: 'kormos',
        required: 10,
        current: 0,
        completed: false
      }
    ],
    rewards: {
      exp: 200,
      gold: 100
    },
    requiredQuests: ['act1_intro'],
    giverNpcId: 'kam_batur',
    startDialog: 'Orman Kormos yaratıklarıyla doldu. Bu karanlık ruhlar Erlik\'in en zayıf uşakları. Onları temizle ki köylüler güvende olsun.',
    progressDialog: 'Hâlâ Kormos\'lar ortalıkta dolaşıyor. Temizlemeye devam et.',
    completeDialog: 'Aferin, savaşçı! Ama bu sadece başlangıçtı. Daha güçlü yaratıklar var ormanda.'
  },
  {
    id: 'act1_elite_hunt',
    name: 'Elit Avı',
    description: 'Daha güçlü düşmanlar ortaya çıktı. Al-Kormos ve Karabasan\'ları avla.',
    act: 1,
    chapter: 3,
    status: 'locked',
    objectives: [
      {
        id: 'kill_al_kormos',
        type: 'kill_enemy',
        description: 'Al-Kormos öldür',
        target: 'al_kormos',
        required: 5,
        current: 0,
        completed: false
      },
      {
        id: 'kill_karabasan',
        type: 'kill_enemy',
        description: 'Karabasan öldür',
        target: 'karabasan',
        required: 5,
        current: 0,
        completed: false
      }
    ],
    rewards: {
      exp: 400,
      gold: 200,
      skillPoints: 1
    },
    requiredQuests: ['act1_first_blood'],
    requiredLevel: 5,
    giverNpcId: 'kam_batur',
    startDialog: 'Kormos\'lardan daha tehlikeli yaratıklar belirdi. Al-Kormos\'lar daha güçlü ve kurnaz. Karabasan\'lar ise gölgelerde pusuya yatıyor. Dikkatli ol.',
    progressDialog: 'Al-Kormos ve Karabasan\'ları henüz temizlemedin. Orman tehlikeli olmaya devam ediyor.',
    completeDialog: 'İyi iş çıkardın! Ama şamanların rüyalarında bir şey gördüm... Bataklıkta Albastı\'lar toplanıyor.'
  },
  {
    id: 'act1_swamp_journey',
    name: 'Bataklık Yolculuğu',
    description: 'Ruh Bataklığı\'na git ve Albastı\'ları araştır.',
    act: 1,
    chapter: 4,
    status: 'locked',
    objectives: [
      {
        id: 'reach_swamp',
        type: 'reach_zone',
        description: 'Ruh Bataklığı\'na ulaş',
        target: 'ruh_batakligi',
        required: 1,
        current: 0,
        completed: false
      },
      {
        id: 'kill_albasti',
        type: 'kill_enemy',
        description: 'Albastı öldür',
        target: 'albasti',
        required: 8,
        current: 0,
        completed: false
      }
    ],
    rewards: {
      exp: 500,
      gold: 250,
      passivePoints: 1
    },
    requiredQuests: ['act1_elite_hunt'],
    requiredLevel: 8,
    giverNpcId: 'kam_batur',
    startDialog: 'Bataklığın derinliklerinde Albastı\'lar yuvalanmış. Bu kötü ruhlar insanların korkularından besleniyor. Oraya git ve kaynağı bul.',
    progressDialog: 'Bataklıkta dikkatli ol. Albastı\'lar aldatıcıdır.',
    completeDialog: 'Albastı\'lar bir Kraliçe\'ye hizmet ediyormuş. Erlik Tapınağı\'nda yuvalanmış. Onu durdurmalısın!'
  },
  {
    id: 'act1_boss',
    name: 'Kraliçenin Düşüşü',
    description: 'Erlik Tapınağı\'nda Albastı Kraliçesi\'ni yenmeli ve Act 1\'i tamamlamalısın.',
    act: 1,
    chapter: 5,
    status: 'locked',
    objectives: [
      {
        id: 'reach_temple',
        type: 'reach_zone',
        description: 'Erlik Tapınağı\'na ulaş',
        target: 'erlik_tapinagi',
        required: 1,
        current: 0,
        completed: false
      },
      {
        id: 'kill_albasti_boss',
        type: 'kill_boss',
        description: 'Albastı Kraliçesi\'ni yen',
        target: 'albasti_kralicesi',
        required: 1,
        current: 0,
        completed: false
      }
    ],
    rewards: {
      exp: 1000,
      gold: 500,
      skillPoints: 1,
      passivePoints: 2
    },
    requiredQuests: ['act1_swamp_journey'],
    requiredLevel: 10,
    giverNpcId: 'kam_batur',
    startDialog: 'Albastı Kraliçesi Erlik Tapınağı\'nda bekliyor. Çok güçlü ama yenilmez değil. Hazırlan ve tapınağa git. Tengri seninle olsun.',
    progressDialog: 'Tapınağa ulaş ve Kraliçe\'yi yen. Köyümüzün kaderi senin ellerinde.',
    completeDialog: 'Albastı Kraliçesi yenildi! Sen gerçek bir Alp\'sin. Ama bu sadece başlangıçtı... Ergenekon\'un dağlarının ardında daha büyük bir tehlike bekliyor.'
  },

  // ===================
  // ACT 2 - ERGENEKON
  // ===================
  {
    id: 'act2_intro',
    name: 'Ergenekon\'a Giriş',
    description: 'Efsanevi Ergenekon vadisine gir ve yeni tehditleri keşfet.',
    act: 2,
    chapter: 1,
    status: 'locked',
    objectives: [
      {
        id: 'reach_ergenekon',
        type: 'reach_zone',
        description: 'Ergenekon Girişi\'ne ulaş',
        target: 'ergenekon_girisi',
        required: 1,
        current: 0,
        completed: false
      },
      {
        id: 'kill_iron_wolves',
        type: 'kill_enemy',
        description: 'Demir Kurt öldür',
        target: 'demir_kurt',
        required: 10,
        current: 0,
        completed: false
      }
    ],
    rewards: {
      exp: 800,
      gold: 400
    },
    requiredQuests: ['act1_boss'],
    requiredLevel: 12,
    giverNpcId: 'kam_batur'
  },
  {
    id: 'act2_elemental_hunt',
    name: 'Element Avı',
    description: 'Ergenekon\'un elemental yaratıklarını avla.',
    act: 2,
    chapter: 2,
    status: 'locked',
    objectives: [
      {
        id: 'kill_fire',
        type: 'kill_enemy',
        description: 'Ateş İblisi öldür',
        target: 'ates_iblisi',
        required: 8,
        current: 0,
        completed: false
      },
      {
        id: 'kill_wind',
        type: 'kill_enemy',
        description: 'Rüzgar Ruhu öldür',
        target: 'ruzgar_ruhu',
        required: 8,
        current: 0,
        completed: false
      }
    ],
    rewards: {
      exp: 1000,
      gold: 500,
      skillPoints: 1
    },
    requiredQuests: ['act2_intro'],
    requiredLevel: 14
  },
  {
    id: 'act2_giants',
    name: 'Devlerin Düşüşü',
    description: 'Dağ Devleri ve Kaya Golemleri Ergenekon\'u tehdit ediyor.',
    act: 2,
    chapter: 3,
    status: 'locked',
    objectives: [
      {
        id: 'kill_giants',
        type: 'kill_enemy',
        description: 'Dağ Devi öldür',
        target: 'dag_devi',
        required: 5,
        current: 0,
        completed: false
      },
      {
        id: 'kill_golems',
        type: 'kill_enemy',
        description: 'Kaya Golemi öldür',
        target: 'kaya_golemi',
        required: 5,
        current: 0,
        completed: false
      }
    ],
    rewards: {
      exp: 1200,
      gold: 600
    },
    requiredQuests: ['act2_elemental_hunt'],
    requiredLevel: 16
  },
  {
    id: 'act2_boss',
    name: 'Yer-Sub\'un Gazabı',
    description: 'Yer ve Su\'nun efendisi Yer-Sub\'u yen.',
    act: 2,
    chapter: 4,
    status: 'locked',
    objectives: [
      {
        id: 'reach_cave',
        type: 'reach_zone',
        description: 'Yer-Sub Tahtı\'na ulaş',
        target: 'yersub_tahti',
        required: 1,
        current: 0,
        completed: false
      },
      {
        id: 'kill_yersub',
        type: 'kill_boss',
        description: 'Yer-Sub\'u yen',
        target: 'yer_sub',
        required: 1,
        current: 0,
        completed: false
      }
    ],
    rewards: {
      exp: 2000,
      gold: 1000,
      skillPoints: 1,
      passivePoints: 2
    },
    requiredQuests: ['act2_giants'],
    requiredLevel: 18,
    completeDialog: 'Yer-Sub yenildi! Yeraltı dünyasına giden yol açıldı...'
  },

  // ===================
  // ACT 3 - YERALTI
  // ===================
  {
    id: 'act3_intro',
    name: 'Karanlık Derinlikler',
    description: 'Yeraltı dünyasına in ve gölge avcılarıyla yüzleş.',
    act: 3,
    chapter: 1,
    status: 'locked',
    objectives: [
      {
        id: 'reach_tunnel',
        type: 'reach_zone',
        description: 'Karanlık Tünel\'e ulaş',
        target: 'karanlik_tunel',
        required: 1,
        current: 0,
        completed: false
      },
      {
        id: 'kill_hunters',
        type: 'kill_enemy',
        description: 'Gölge Avcısı öldür',
        target: 'golge_avcisi',
        required: 10,
        current: 0,
        completed: false
      }
    ],
    rewards: {
      exp: 1500,
      gold: 750
    },
    requiredQuests: ['act2_boss'],
    requiredLevel: 20
  },
  {
    id: 'act3_undead',
    name: 'Ölümsüz Ordu',
    description: 'Kemik savaşçıları ve lanetli ruhları temizle.',
    act: 3,
    chapter: 2,
    status: 'locked',
    objectives: [
      {
        id: 'kill_skeletons',
        type: 'kill_enemy',
        description: 'Kemik Savaşçı öldür',
        target: 'kemik_savasci',
        required: 15,
        current: 0,
        completed: false
      },
      {
        id: 'kill_spirits',
        type: 'kill_enemy',
        description: 'Lanetli Ruh öldür',
        target: 'lanetli_ruh',
        required: 10,
        current: 0,
        completed: false
      }
    ],
    rewards: {
      exp: 1800,
      gold: 900,
      skillPoints: 1
    },
    requiredQuests: ['act3_intro'],
    requiredLevel: 22
  },
  {
    id: 'act3_heralds',
    name: 'Ölümün Habercileri',
    description: 'Ölüm Habercileri Erlik\'in gelişini müjdeliyor.',
    act: 3,
    chapter: 3,
    status: 'locked',
    objectives: [
      {
        id: 'kill_heralds',
        type: 'kill_enemy',
        description: 'Ölüm Habercisi öldür',
        target: 'olum_habercisi',
        required: 8,
        current: 0,
        completed: false
      }
    ],
    rewards: {
      exp: 2000,
      gold: 1000
    },
    requiredQuests: ['act3_undead'],
    requiredLevel: 25
  },
  {
    id: 'act3_boss',
    name: 'Elçinin Sonu',
    description: 'Lanetli Saray\'da Erlik\'in Elçisi\'ni yen.',
    act: 3,
    chapter: 4,
    status: 'locked',
    objectives: [
      {
        id: 'reach_palace',
        type: 'reach_zone',
        description: 'Lanetli Saray\'a ulaş',
        target: 'lanetli_saray',
        required: 1,
        current: 0,
        completed: false
      },
      {
        id: 'kill_messenger',
        type: 'kill_boss',
        description: 'Erlik Elçisi\'ni yen',
        target: 'erlik_elcisi',
        required: 1,
        current: 0,
        completed: false
      }
    ],
    rewards: {
      exp: 3000,
      gold: 1500,
      skillPoints: 2,
      passivePoints: 3
    },
    requiredQuests: ['act3_heralds'],
    requiredLevel: 28,
    completeDialog: 'Erlik\'in Elçisi yenildi! Artık Erlik\'in diyarına girebilirsin...'
  },

  // ===================
  // ACT 4 - ERLIK DIYARI
  // ===================
  {
    id: 'act4_intro',
    name: 'Cehennemin Kapısı',
    description: 'Erlik\'in diyarına gir ve onun şövalyelerine meydan oku.',
    act: 4,
    chapter: 1,
    status: 'locked',
    objectives: [
      {
        id: 'reach_gate',
        type: 'reach_zone',
        description: 'Erlik Kapısı\'na ulaş',
        target: 'erlik_kapisi',
        required: 1,
        current: 0,
        completed: false
      },
      {
        id: 'kill_knights',
        type: 'kill_enemy',
        description: 'Erlik Şövalyesi öldür',
        target: 'erlik_sovalyesi',
        required: 10,
        current: 0,
        completed: false
      }
    ],
    rewards: {
      exp: 2500,
      gold: 1250
    },
    requiredQuests: ['act3_boss'],
    requiredLevel: 30
  },
  {
    id: 'act4_fallen',
    name: 'Düşmüş Melekler',
    description: 'Karanlık Serafları yok et.',
    act: 4,
    chapter: 2,
    status: 'locked',
    objectives: [
      {
        id: 'kill_seraphs',
        type: 'kill_enemy',
        description: 'Karanlık Seraf öldür',
        target: 'karanlik_seraf',
        required: 12,
        current: 0,
        completed: false
      }
    ],
    rewards: {
      exp: 3000,
      gold: 1500,
      skillPoints: 1
    },
    requiredQuests: ['act4_intro'],
    requiredLevel: 33
  },
  {
    id: 'act4_garden',
    name: 'Cehennem Bahçesi',
    description: 'Erlik\'in korkunç bahçesini geç.',
    act: 4,
    chapter: 3,
    status: 'locked',
    objectives: [
      {
        id: 'reach_garden',
        type: 'reach_zone',
        description: 'Cehennem Bahçesi\'ne ulaş',
        target: 'cehennem_bahcesi',
        required: 1,
        current: 0,
        completed: false
      },
      {
        id: 'survive_garden',
        type: 'kill_enemy',
        description: 'Düşman öldür',
        target: 'erlik_sovalyesi',
        required: 15,
        current: 0,
        completed: false
      }
    ],
    rewards: {
      exp: 3500,
      gold: 1750
    },
    requiredQuests: ['act4_fallen'],
    requiredLevel: 35
  },
  {
    id: 'act4_final',
    name: 'Yeraltı Hükümdarı',
    description: 'Taht Odası\'nda Erlik Han ile son savaş. Dünyayı kurtar!',
    act: 4,
    chapter: 4,
    status: 'locked',
    objectives: [
      {
        id: 'reach_throne',
        type: 'reach_zone',
        description: 'Taht Odası\'na ulaş',
        target: 'taht_odasi',
        required: 1,
        current: 0,
        completed: false
      },
      {
        id: 'kill_erlik',
        type: 'kill_boss',
        description: 'ERLIK HAN\'I YEN',
        target: 'erlik_han',
        required: 1,
        current: 0,
        completed: false
      }
    ],
    rewards: {
      exp: 10000,
      gold: 5000,
      skillPoints: 3,
      passivePoints: 5
    },
    requiredQuests: ['act4_garden'],
    requiredLevel: 38,
    completeDialog: 'ERLİK HAN YENİLDİ! Dünya kurtarıldı! Sen bir efsane oldun, kahraman!'
  }
];

// ==========================================
// SIDE QUESTS - ACT 1
// ==========================================

export const SIDE_QUESTS: Quest[] = [
  {
    id: 'side_kayip_devriye',
    name: 'Kayıp Devriye',
    description: 'Muhafız Börü\'nün kayıp devriye ekibinin izini bul. Karanlık Orman\'daki terk edilmiş kampı araştır.',
    act: 1,
    chapter: 10,
    status: 'locked',
    objectives: [
      {
        id: 'find_camp',
        type: 'reach_zone',
        description: 'Terk edilmiş kampı bul',
        target: 'karanlik_orman',
        required: 1,
        current: 0,
        completed: false
      },
      {
        id: 'return_boru',
        type: 'talk_to_npc',
        description: 'Muhafız Börü\'ye rapor ver',
        target: 'muhafiz_koruyucu',
        required: 1,
        current: 0,
        completed: false
      }
    ],
    rewards: {
      exp: 150,
      gold: 75
    },
    requiredQuests: ['act1_first_blood'],
    giverNpcId: 'muhafiz_koruyucu',
    turnInNpcId: 'muhafiz_koruyucu',
    startDialog: 'Devriye ekibim ormanın derinliklerinde kayboldu. Kamplarını bulabilir misin?',
    completeDialog: 'Pusuya düşmüşler... Ruhları huzur bulsun. Teşekkürler savaşçı, al bu iksiri.'
  },
  {
    id: 'side_ruh_damlaları',
    name: 'Ruh Damlaları',
    description: 'Yaralı izci, iyileşmek için Karabasanlardan düşen Ruh Damlalarına ihtiyaç duyuyor.',
    act: 1,
    chapter: 11,
    status: 'locked',
    objectives: [
      {
        id: 'collect_drops',
        type: 'collect_item',
        description: 'Ruh Damlası topla',
        target: 'ruh_damlasi',
        required: 3,
        current: 0,
        completed: false
      }
    ],
    rewards: {
      exp: 250,
      gold: 100
    },
    requiredQuests: ['act1_swamp_journey'],
    giverNpcId: 'kayip_izci',
    turnInNpcId: 'kayip_izci',
    startDialog: 'Ruh Damlalarına ihtiyacım var. Karabasanları öldürünce düşürürler. 3 tane yeterli.',
    completeDialog: 'Tengri\'ye şükür! Yaralarım iyileşecek. Al bu yüzüğü, sana yardımcı olsun.'
  },
  {
    id: 'side_kara_gulle_bilmecesi',
    name: 'Kara Gülle\'nin Bilmecesi',
    description: 'Deli Kam Kara Gülle, kuzeydoğu adasındaki harabeden Erlik İdolü istiyor. Karşılığında güçlü bir tılsım yapacak.',
    act: 1,
    chapter: 12,
    status: 'locked',
    objectives: [
      {
        id: 'get_idol',
        type: 'collect_item',
        description: 'Erlik İdolü\'nü bul',
        target: 'erlik_idolu',
        required: 1,
        current: 0,
        completed: false
      },
      {
        id: 'return_gulle',
        type: 'talk_to_npc',
        description: 'Kara Gülle\'ye götür',
        target: 'kara_gulle',
        required: 1,
        current: 0,
        completed: false
      }
    ],
    rewards: {
      exp: 300,
      gold: 150
    },
    requiredQuests: ['act1_swamp_journey'],
    giverNpcId: 'kara_gulle',
    turnInNpcId: 'kara_gulle',
    startDialog: 'Kuzeydoğu adasındaki harabede bir idol var. Erlik İdolü! Getir bana!',
    completeDialog: '*kıkırdayarak* Hihihi! Güzel, güzel! Al sana tılsım. Boss\'lara karşı işe yarar!'
  },
  // --- New Act 1 Side Quests ---
  {
    id: 'side_lost_hunter',
    name: 'Kayıp Avcı',
    description: 'Karanlık Orman\'da kayıp bir avcı var. Onu bulup köye geri getir.',
    act: 1,
    chapter: 13,
    status: 'locked',
    objectives: [
      {
        id: 'find_hunter',
        type: 'reach_zone',
        description: 'Karanlık Orman\'da avcıyı bul',
        target: 'karanlik_orman',
        required: 1,
        current: 0,
        completed: false
      },
      {
        id: 'kill_wolves',
        type: 'kill_enemy',
        description: 'Avcıyı kuşatan kurtları öldür',
        target: 'kormos',
        required: 5,
        current: 0,
        completed: false
      },
    ],
    rewards: { exp: 300, gold: 150, itemIds: ['kam_boyasi_x3'] },
    requiredQuests: ['act1_intro'],
    requiredLevel: 3,
    giverNpcId: 'korkut_ata',
    startDialog: 'Genç savaşçı, ormana giden avcımızdan haber alamıyoruz. Onu bulup getirir misin?',
    progressDialog: 'Avcıyı buldun mu? Umarım sağ salimdir.',
    completeDialog: 'Avcıyı kurtardın! Köy sana minnettardır.',
  },
  {
    id: 'side_poisoned_water',
    name: 'Zehirli Su',
    description: 'Ruh Bataklığı\'ndaki su kaynakları zehirlenmiş. Kaynağı temizle.',
    act: 1,
    chapter: 14,
    status: 'locked',
    objectives: [
      {
        id: 'reach_swamp',
        type: 'reach_zone',
        description: 'Ruh Bataklığı\'na git',
        target: 'ruh_batakligi',
        required: 1,
        current: 0,
        completed: false
      },
      {
        id: 'kill_swamp',
        type: 'kill_enemy',
        description: 'Zehirli Albastı\'ları temizle',
        target: 'albasti',
        required: 5,
        current: 0,
        completed: false
      },
    ],
    rewards: { exp: 400, gold: 200 },
    requiredQuests: ['act1_first_blood'],
    requiredLevel: 6,
    giverNpcId: 'kam_ana',
    startDialog: 'Bataklıktaki sular zehirlenmiş. Bu Albastı\'ların işi... Onları temizle.',
    progressDialog: 'Zehir hâlâ suda. Daha fazla Albastı öldürmelisin.',
    completeDialog: 'Sular tekrar temiz! Kam Ana sana şifa duası okuyor.',
  },
  {
    id: 'side_old_graveyard',
    name: 'Eski Mezarlık',
    description: 'Erlik Tapınağı\'nın yakınındaki mezarlıkta huzursuz ruhlar var.',
    act: 1,
    chapter: 15,
    status: 'locked',
    objectives: [
      {
        id: 'reach_temple',
        type: 'reach_zone',
        description: 'Erlik Tapınağı\'na git',
        target: 'erlik_tapinagi',
        required: 1,
        current: 0,
        completed: false
      },
      {
        id: 'kill_undead',
        type: 'kill_enemy',
        description: 'Huzursuz ruhları yok et',
        target: 'karabasan',
        required: 15,
        current: 0,
        completed: false
      },
    ],
    rewards: { exp: 600, gold: 300, skillPoints: 1 },
    requiredQuests: ['act1_elite_hunt'],
    requiredLevel: 8,
    giverNpcId: 'alp_turan',
    startDialog: 'Tapınağın yakınında ölüler uyanıyor. Git ve onları tekrar toprağa gönder!',
    progressDialog: 'Mezarlık hâlâ tehlikeli. Devam et savaşçı.',
    completeDialog: 'Mezarlık temizlendi. İşte sana bu mücadelenin karşılığı.',
  },
];

// Combined quest list for easy access
export const ALL_QUESTS: Quest[] = [...MAIN_QUESTS, ...SIDE_QUESTS];

// Quest Manager class
export class QuestManager {
  private quests: Map<string, Quest> = new Map();
  private activeQuests: Set<string> = new Set();
  private completedQuests: Set<string> = new Set();

  // Callbacks
  private onQuestUpdate: ((quest: Quest) => void) | null = null;
  private onQuestComplete: ((quest: Quest) => void) | null = null;

  constructor() {
    // Initialize quests from definition
    for (const quest of MAIN_QUESTS) {
      this.quests.set(quest.id, { ...quest });
    }
    for (const quest of SIDE_QUESTS) {
      this.quests.set(quest.id, { ...quest });
    }
  }

  setOnQuestUpdate(callback: (quest: Quest) => void): void {
    this.onQuestUpdate = callback;
  }

  setOnQuestComplete(callback: (quest: Quest) => void): void {
    this.onQuestComplete = callback;
  }

  getQuest(id: string): Quest | undefined {
    return this.quests.get(id);
  }

  getAllQuests(): Quest[] {
    return Array.from(this.quests.values());
  }

  getActiveQuests(): Quest[] {
    return Array.from(this.quests.values()).filter(q => q.status === 'active');
  }

  getAvailableQuests(): Quest[] {
    return Array.from(this.quests.values()).filter(q => q.status === 'available');
  }

  getCompletedQuests(): Quest[] {
    return Array.from(this.quests.values()).filter(q => q.status === 'completed');
  }

  getQuestsForAct(act: number): Quest[] {
    return Array.from(this.quests.values())
      .filter(q => q.act === act)
      .sort((a, b) => a.chapter - b.chapter);
  }

  // Check and update quest availability based on prerequisites
  updateQuestAvailability(playerLevel: number): void {
    for (const quest of this.quests.values()) {
      if (quest.status !== 'locked') continue;

      // Check level requirement
      if (quest.requiredLevel && playerLevel < quest.requiredLevel) {
        continue;
      }

      // Check required quests
      if (quest.requiredQuests) {
        const allComplete = quest.requiredQuests.every(qid =>
          this.completedQuests.has(qid)
        );
        if (!allComplete) continue;
      }

      // Quest is now available
      quest.status = 'available';
      if (this.onQuestUpdate) {
        this.onQuestUpdate(quest);
      }
    }
  }

  // Accept a quest
  acceptQuest(questId: string): boolean {
    const quest = this.quests.get(questId);
    if (!quest || quest.status !== 'available') return false;

    quest.status = 'active';
    this.activeQuests.add(questId);

    if (this.onQuestUpdate) {
      this.onQuestUpdate(quest);
    }

    return true;
  }

  // Update quest progress
  updateProgress(type: ObjectiveType, target: string, amount: number = 1): void {
    for (const questId of this.activeQuests) {
      const quest = this.quests.get(questId);
      if (!quest) continue;

      for (const objective of quest.objectives) {
        if (objective.completed) continue;
        if (objective.type !== type) continue;
        if (objective.target !== target) continue;

        objective.current = Math.min(objective.current + amount, objective.required);

        if (objective.current >= objective.required) {
          objective.completed = true;
        }

        if (this.onQuestUpdate) {
          this.onQuestUpdate(quest);
        }

        // Check if all objectives are complete
        this.checkQuestCompletion(questId);
      }
    }
  }

  // Check if a quest is fully complete
  private checkQuestCompletion(questId: string): void {
    const quest = this.quests.get(questId);
    if (!quest) return;

    const allComplete = quest.objectives.every(obj => obj.completed);
    if (allComplete) {
      // Quest ready to turn in (or auto-complete if no turn-in NPC)
      if (!quest.turnInNpcId) {
        this.completeQuest(questId);
      }
    }
  }

  // Complete a quest and give rewards
  completeQuest(questId: string): QuestReward | null {
    const quest = this.quests.get(questId);
    if (!quest) return null;

    const allComplete = quest.objectives.every(obj => obj.completed);
    if (!allComplete) return null;

    quest.status = 'completed';
    this.activeQuests.delete(questId);
    this.completedQuests.add(questId);

    if (this.onQuestComplete) {
      this.onQuestComplete(quest);
    }

    // Update availability of other quests
    this.updateQuestAvailability(99); // Pass high level to check all

    return quest.rewards;
  }

  // Check if a specific quest is completed
  isQuestCompleted(questId: string): boolean {
    return this.completedQuests.has(questId);
  }

  // Get quest marker state for an NPC
  getNpcQuestMarker(npcId: string): 'available' | 'active' | 'completable' | 'none' {
    // Check for completable quests first (highest priority)
    for (const quest of this.quests.values()) {
      if (quest.status === 'active' && quest.turnInNpcId === npcId) {
        const allComplete = quest.objectives.every(obj => obj.completed);
        if (allComplete) return 'completable';
      }
    }

    // Check for active quests given by this NPC
    for (const quest of this.quests.values()) {
      if (quest.status === 'active' && quest.giverNpcId === npcId) {
        const allComplete = quest.objectives.every(obj => obj.completed);
        if (allComplete) return 'completable'; // Auto-complete quests show as completable on giver
        return 'active';
      }
    }

    // Check for available quests from this NPC
    for (const quest of this.quests.values()) {
      if (quest.status === 'available' && quest.giverNpcId === npcId) {
        return 'available';
      }
    }

    return 'none';
  }

  // Serialization for save/load
  serialize(): object {
    return {
      quests: Array.from(this.quests.entries()).map(([id, quest]) => ({
        id,
        status: quest.status,
        objectives: quest.objectives.map(obj => ({
          id: obj.id,
          current: obj.current,
          completed: obj.completed
        }))
      })),
      activeQuests: Array.from(this.activeQuests),
      completedQuests: Array.from(this.completedQuests)
    };
  }

  deserialize(data: any): void {
    if (!data) return;

    // Restore quest states
    for (const questData of data.quests || []) {
      const quest = this.quests.get(questData.id);
      if (!quest) continue;

      quest.status = questData.status;

      for (const objData of questData.objectives || []) {
        const objective = quest.objectives.find(o => o.id === objData.id);
        if (objective) {
          objective.current = objData.current;
          objective.completed = objData.completed;
        }
      }
    }

    this.activeQuests = new Set(data.activeQuests || []);
    this.completedQuests = new Set(data.completedQuests || []);
  }
}
